import { HTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

/**
 * Console Batch 2 status pill — tinted background, colored label, leading dot.
 * Six semantic tones (Phase 2 design system). Legacy variant names are kept
 * as aliases so every existing call site renders unchanged, just restyled.
 */
type Variant =
    | 'default'
    | 'secondary'
    | 'success'
    | 'danger'
    | 'muted'
    | 'accent'
    | 'info'
    | 'warning'
    | 'neutral';

type Tone = 'accent' | 'success' | 'danger' | 'warning' | 'info' | 'neutral';

const VARIANT_TONE: Record<Variant, Tone> = {
    default: 'accent',
    accent: 'accent',
    secondary: 'neutral',
    muted: 'neutral',
    neutral: 'neutral',
    success: 'success',
    danger: 'danger',
    warning: 'warning',
    info: 'info',
};

const TONE_BG: Record<Tone, string> = {
    accent: 'bg-tg-orange/10 text-tg-orange',
    success: 'bg-tg-teal/10 text-tg-teal-dk',
    danger: 'bg-tg-danger/10 text-[#B91C1C]',
    warning: 'bg-tg-warning/12 text-[#92400E]',
    info: 'bg-tg-info/10 text-[#4338CA]',
    neutral: 'bg-tg-surface-2 text-tg-muted',
};

const TONE_DOT: Record<Tone, string> = {
    accent: 'bg-tg-orange',
    success: 'bg-tg-teal',
    danger: 'bg-tg-danger',
    warning: 'bg-tg-warning',
    info: 'bg-tg-info',
    neutral: 'bg-tg-subtle',
};

export function Badge({
    variant = 'default',
    dot = true,
    className,
    children,
    ...props
}: HTMLAttributes<HTMLSpanElement> & { variant?: Variant; dot?: boolean }) {
    const tone = VARIANT_TONE[variant];
    return (
        <span
            className={cn(
                'inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11.5px] font-semibold leading-5',
                TONE_BG[tone],
                className,
            )}
            {...props}
        >
            {dot && <span className={cn('h-1.5 w-1.5 shrink-0 rounded-full', TONE_DOT[tone])} />}
            {children}
        </span>
    );
}
